export type WorkflowStateEntryDependency = {
    key: string;
    source: string;
    target: string;
};

export type WorkflowStateEntryPropagation<TValue> = {
    dependency: WorkflowStateEntryDependency;
    value: TValue;
};

export function deriveWorkflowStateEntryPropagation<TValue>(args: {
    dependencies: readonly WorkflowStateEntryDependency[];
    sources: ReadonlyMap<string, TValue>;
    previouslyMaterialized: ReadonlySet<string>;
    /** Undefined means this dependency does not newly materialize its target. */
    resolveTarget: (dependency: WorkflowStateEntryDependency, sourceValue: TValue) => TValue | undefined;
}): WorkflowStateEntryPropagation<TValue>[][] {
    const visited = new Set([...args.previouslyMaterialized, ...args.sources.keys()]);
    const hops: WorkflowStateEntryPropagation<TValue>[][] = [];
    let sources = args.sources;
    while (sources.size > 0) {
        const next = new Map<string, TValue>();
        const hop: WorkflowStateEntryPropagation<TValue>[] = [];
        for (const dependency of args.dependencies) {
            if (!sources.has(dependency.source) || visited.has(dependency.target)) continue;
            const sourceValue = sources.get(dependency.source);
            if (sourceValue === undefined) continue;
            const value = args.resolveTarget(dependency, sourceValue);
            if (value === undefined) continue;
            visited.add(dependency.target);
            next.set(dependency.target, value);
            hop.push({ dependency, value });
        }
        if (hop.length === 0) break;
        hops.push(hop);
        sources = next;
    }
    return hops;
}
